import { groupBy, sortBy } from 'lodash'

const CATEGORY_META = {
  NonBlocking:          { uiPrefix: '',       categoryColor: '#909399' },
  MaterialProvisioning: { uiPrefix: '',       categoryColor: '#409EFF' },
  Internal:             { uiPrefix: '',       categoryColor: '#606266' },
  External:             { uiPrefix: '(Ext)',  categoryColor: '#E6A23C' },
  Other:                { uiPrefix: '(Oth)',  categoryColor: '#909399' },
}

const NODE_TYPE_BY_CATEGORY = {
  NonBlocking:          'nonblockingNode',
  MaterialProvisioning: 'materialNode',
  Internal:             'actionNode',
  External:             'externalNode',
  Other:                'otherNode',
}

// Wire-model field name accessors. The wire shape differs from the spec — the
// codebase uses idx/category/actionType/durationInMinutes/company. See CLAUDE.md
// rule "verify field names before assuming".
function actionIdx(a)        { return a.idx ?? a.index ?? 0 }
function actionCategory(a)   { return a.category || a.workcenterGroupCategory || 'Internal' }
function actionLabel(a)      { return a.actionType || a.name || '' }
function actionDuration(a)   { return a.durationInMinutes ?? a.estimatedDurationMinutes ?? 0 }
function actionWorkcenterName(ctx, a) { 
  if( a.category == 'External' )
    return a.comments
  if(ctx.workcenterNamesMap)
    return ctx.workcenterNamesMap.get(String(a.workcenterId)) ?? ''
  return ''
}

function actionWorkcenterGroupName(ctx, action) {
  return ctx.wcGroupByActionTypeId
        ? ctx.wcGroupByActionTypeId.get(String(action.actionTypeId)) || '' 
        : ''
}

function getStatusClass(globalMetadata, action) {
      if (action.alerts && action.alerts.length > 0) {
        return `in-progress-warning item-alerts`
      }

      if(action.remainingDurationInMinutes < 0 && !action.isCompleted) {
        return `in-progress-warning item-alerts`
      }

      if (action.status) {
        var st = globalMetadata.statuses[action.status]
        if (st) 
          return st.class
      }

      console.warn('Unknown status for action', action)

      return ''
}

function actionWorkerNames(a) {
  if (!Array.isArray(a.workers)) return ''
  return a.workers.map(w => w.workerName || w.text || '').join(', ')
}

// Component shape varies: OrderItem has `actions` + `children` at top level (with
// each child also being an OrderItem). InquiryItem keeps actions/children inside
// `.component`. Normalize so the builder sees a single shape.
function getActions(node)  { return node?.actions || node?.component?.actions || [] }
function getChildren(node) { return node?.children || node?.component?.children || [] }

/**
 * Build VueFlow { nodes, edges } from a Component / Position / Order root entity.
 *
 * @param {Object} rootComponent
 * @param {Object} opts
 *   - statusByActionId: Map<actionId, statusString>  (execution-time entities)
 *   - workcenterGroups: Array of { name, actionTypeIds[] }  (for WC-group lookup)
 *   - useQuantity: boolean — multiply duration by ancestor quantity chain
 *   - showStatus: boolean — color headers by status (orders/positions only)
 *   - depth: number — safety cap, default 6
 * @returns {{ nodes: Array, edges: Array }}
 */
export function buildGraph(rootComponent, opts = {}) {
  const ctx = {
    nodes: [],
    edges: [],
    opts: { depth: 6, ...opts },
    edgeCounter: 0,
    cyclicWarned: false,
    useQuantity: opts.useQuantity === true,
    showStatus: opts.showStatus === true,
    globalMetadata: opts.globalMetadata || {},
    wcGroupByActionTypeId: buildWcGroupLookup(opts.globalMetadata),
    workcenterNamesMap: buildWcNamesLookup(opts.globalMetadata),
  }

  pushNode(ctx, {
    id: '__start__',
    type: 'startNode',
    data: { label: 'Start', kind: 'start' },
    position: { x: 0, y: 0 },
  })

  const rootQty = ctx.useQuantity && typeof rootComponent?.quantity === 'number'
    ? rootComponent.quantity
    : 1

  // The last main-flow action(s) are the natural terminators — no synthetic
  // "Finish" node. Start remains as the anchor for dashed Material/NonBlocking
  // edges and as a visible entry point.
  walkComponent(ctx, rootComponent, '__start__', null, 0, new Set(), rootQty)

  return { nodes: ctx.nodes, edges: ctx.edges }
}

// Build actionTypeId → workcenterGroupName lookup from the parent form's loaded list.
function buildWcGroupLookup(globalMetadata) {
  const workcenterGroups = globalMetadata.resources ? globalMetadata.resources.workcenterGroups : []
  const map = new Map()
  if (!Array.isArray(workcenterGroups)) return map
  for (const g of workcenterGroups) {
    const name = g.name || g.text || ''
    if (!Array.isArray(g.actionTypeIds)) continue
    for (const id of g.actionTypeIds) {
      map.set(String(id), name)
    }
  }

  return map
}

function buildWcNamesLookup(globalMetadata) {
  const workcenterGroups = globalMetadata.resources ? globalMetadata.resources.workcenterGroups : []
  const map = new Map()
  if (!Array.isArray(workcenterGroups)) return map
  for (const g of workcenterGroups) {
    for(const wc of g.workcenters || []) {
      map.set(String(wc.id), wc.name)
    }
  }

  return map
}

function walkComponent(ctx, component, entryId, parentGroupId, depth, seen, cumulativeQty) {
  if (!component) return [entryId]
  if (depth > ctx.opts.depth) {
    if (!ctx.cyclicWarned) {
      // eslint-disable-next-line no-console
      console.warn('[workflowgraph] depth cap reached — possible cyclic children')
      ctx.cyclicWarned = true
    }
    return [entryId]
  }
  if (component.id != null && seen.has(component.id)) {
    if (!ctx.cyclicWarned) {
      // eslint-disable-next-line no-console
      console.warn('[workflowgraph] cyclic child detected, stopping recursion')
      ctx.cyclicWarned = true
    }
    return [entryId]
  }
  const nextSeen = component.id != null ? new Set([...seen, component.id]) : seen

  const ns = parentGroupId ? parentGroupId : `c_${component.id || 'root'}`

  // `entryId` is the node from which dashed material/non-blocking edges originate,
  // and the fallback "previous layer" when this component has no upstream actions.
  // At the root level it is `__start__`. For sub-components we pass `null` because
  // adding edges to/from a compound (group) node confuses dagre's ranker
  // ("can't access property 'rank'"). Sub-component flows start free-floating
  // inside their group and only their *exit* nodes connect to the parent's flow.

  // 1. Render child components first (parent-before-child rule for VueFlow).
  // Only render children that actually carry actions (recursively).
  const childExitIds = []
  const renderableChildren = getChildren(component).filter(c => hasAnyActions(c))
  for (const child of renderableChildren) {
    const groupId = `${ns}_sub_${child.id}`
    pushNode(ctx, {
      id: groupId,
      type: 'subcomponentGroup',
      data: {
        label: child.name || child.productName || child.text || 'Sub-component',
        component: child,
      },
      position: { x: 0, y: 0 },
      parentNode: parentGroupId || undefined,
    })
    const childQty = ctx.useQuantity && typeof child.quantity === 'number' ? child.quantity : 1
    // Pass null as entryId so the sub-flow doesn't try to draw edges from the group node.
    const subExits = walkComponent(
      ctx, child, null, groupId, depth + 1, nextSeen, cumulativeQty * childQty
    )
    childExitIds.push(...subExits)
  }

  // 2. Bucket this component's actions by idx.
  const actions = sortBy(getActions(component), a => actionIdx(a))
  const grouped = groupBy(actions, a => actionIdx(a))
  const sortedIndices = Object.keys(grouped).map(Number).sort((a, b) => a - b)

  // 3. MaterialProvisioning lane (idx -1).
  const materials = grouped[-1] || []
  const materialIds = materials.map(a =>
    addActionNode(ctx, a, component, ns, parentGroupId, cumulativeQty)
  )
  if (entryId) {
    materialIds.forEach(id => addEdge(ctx, entryId, id, { dashed: true }))
  }

  // 4. NonBlocking lane (idx -10, plus any other negative index that's not -1).
  const nonBlocking = []
  for (const idx of sortedIndices) {
    if (idx < 0 && idx !== -1) nonBlocking.push(...grouped[idx])
  }
  const nonBlockingIds = nonBlocking.map(a =>
    addActionNode(ctx, a, component, ns, parentGroupId, cumulativeQty)
  )
  if (entryId) {
    nonBlockingIds.forEach(id => addEdge(ctx, entryId, id, { dashed: true }))
  }

  // 5. Main sequential layers.
  let previousLayer = []
  if (materialIds.length) previousLayer.push(...materialIds)
  if (childExitIds.length) previousLayer.push(...childExitIds)
  // Fall back to entryId only at root level (sub-level entryId is null).
  if (!previousLayer.length && entryId) previousLayer.push(entryId)

  for (const idx of sortedIndices) {
    if (idx < 0) continue
    const layerIds = grouped[idx].map(a =>
      addActionNode(ctx, a, component, ns, parentGroupId, cumulativeQty)
    )
    if (previousLayer.length) {
      previousLayer.forEach(prev =>
        layerIds.forEach(cur => addEdge(ctx, prev, cur))
      )
    }
    previousLayer = layerIds
  }

  // Decide what to return as this component's "exits" — the nodes a caller
  // should connect into. Prefer last main-flow layer, then child exits, then
  // materials/non-blocking, then nothing.
  if (previousLayer.length) return previousLayer
  if (childExitIds.length) return childExitIds
  if (materialIds.length) return materialIds
  if (nonBlockingIds.length) return nonBlockingIds
  return entryId ? [entryId] : []
}

function hasAnyActions(node) {
  if (!node) return false
  if (getActions(node).length > 0) return true
  return getChildren(node).some(c => hasAnyActions(c))
}

function addActionNode(ctx, action, component, ns, parentGroupId, cumulativeQty) {
  const cat = actionCategory(action)
  const meta = CATEGORY_META[cat] || CATEGORY_META.Internal
  const status = ctx.opts.statusByActionId?.get(action.id) || action.status || 'Pending'
  const baseMinutes = actionDuration(action) || 0
  const totalMinutes = ctx.useQuantity ? baseMinutes * cumulativeQty : baseMinutes
  const statusClass = ctx.showStatus ? getStatusClass(ctx.globalMetadata,action) : 'el-table tr'
  const id = `${ns}_a_${action.id}`
  pushNode(ctx, {
    id,
    type: NODE_TYPE_BY_CATEGORY[cat] || 'actionNode',
    data: {
      label: [meta.uiPrefix, actionLabel(action)].filter(Boolean).join(' '),
      action,
      component,
      status,
      statusClass: statusClass,
      duration: baseMinutes,
      totalDuration: totalMinutes,
      quantity: cumulativeQty,
      useQuantity: ctx.useQuantity,
      showStatus: ctx.showStatus,
      workcenter: actionWorkcenterName(ctx, action),
      workcenterGroup: actionWorkcenterGroupName(ctx, action) ,
      workers: actionWorkerNames(action),
      idx: actionIdx(action),
      category: cat,
      comments: action.comments || '',
    },

    position: { x: 0, y: 0 },
    parentNode: parentGroupId || undefined,
  })

  return id
}

function pushNode(ctx, node) { ctx.nodes.push(node) }

function addEdge(ctx, source, target, opts = {}) {
  ctx.edges.push({
    id: `e_${source}__${target}__${ctx.edgeCounter++}`,
    source,
    target,
    type: 'smoothstep',
    animated: opts.animated || false,
    style: opts.dashed ? { strokeDasharray: '5 5' } : undefined,
  })
}
